import { useState } from 'react';
import { Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

import CommonDeleteModal from '../../../../form-components/CommonDeleteModal';
import { deleteContractLogoSeal } from '../../../../api/contract';
import LogoSealEditModal from './LogoSealEditModal';
import {
  ModalContainer,
  ModalHeader,
  HeaderTitle,
  CloseButton,
  ModalContent,
  TabRow,
  TabButton,
  Card,
  Section,
  SectionLabel,
  EmptyBox,
  EmptyText,
  ItemBox,
  ItemName,
  ItemActions,
  EditButton,
  DeleteButton,
  RegisterBox,
  RegisterText,
} from './LogoSealModalStyled';

// 지점의 로고/직인을 직인·로고 탭으로 나눠 N개 관리하는 목록 모달.
// 등록/수정은 LogoSealEditModal(서브 모달), 삭제는 CommonDeleteModal 로 처리한다.
const LogoSealModal = ({ open, branchId, logoSeal, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const token = useSelector(state => state.user.user?.token);

  // 'seal'(직인) / 'logo'(로고)
  const [activeTab, setActiveTab] = useState('seal');
  // 등록/수정 서브 모달 — { type, item } (item 이 null 이면 등록)
  const [editTarget, setEditTarget] = useState(null);
  // 삭제 확인 대상 아이템
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currentType = activeTab === 'seal' ? 'SEAL' : 'LOGO';
  const currentItems = activeTab === 'seal' ? logoSeal?.seals || [] : logoSeal?.logos || [];
  const typeLabel = activeTab === 'seal' ? t('직인') : t('로고');

  const deleteMutation = useMutation({
    mutationFn: id => deleteContractLogoSeal(id, token),
    onSuccess: () => {
      toast.success(t('성공적으로 삭제되었습니다'));
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['contractLogoSeal', branchId] });
    },
  });

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalContainer>
          <ModalHeader>
            <HeaderTitle>{t('로고&직인 등록')}</HeaderTitle>
            <CloseButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            {/* 직인 / 로고 탭 */}
            <TabRow>
              <TabButton side="left" active={activeTab === 'seal'} onClick={() => setActiveTab('seal')}>
                {t('직인')}
              </TabButton>
              <TabButton side="right" active={activeTab === 'logo'} onClick={() => setActiveTab('logo')}>
                {t('로고')}
              </TabButton>
            </TabRow>

            <Card>
              {/* 목록 */}
              <Section>
                <SectionLabel>{t('목록')}</SectionLabel>
                {currentItems.length === 0 ? (
                  <EmptyBox>
                    <EmptyText>{t('등록된 항목이 없습니다.')}</EmptyText>
                  </EmptyBox>
                ) : (
                  currentItems.map(item => (
                    <ItemBox key={item.id}>
                      <ItemName>{item.title || typeLabel}</ItemName>
                      <ItemActions>
                        <EditButton onClick={() => setEditTarget({ type: currentType, item })}>
                          {t('수정')}
                        </EditButton>
                        <DeleteButton onClick={() => setDeleteTarget(item)}>{t('삭제')}</DeleteButton>
                      </ItemActions>
                    </ItemBox>
                  ))
                )}
              </Section>

              {/* 등록 */}
              <Section>
                <SectionLabel>{t('등록')}</SectionLabel>
                <RegisterBox onClick={() => setEditTarget({ type: currentType, item: null })}>
                  <AddIcon sx={{ fontSize: 20, color: '#141414' }} />
                  <RegisterText>{t('서명 또는 직인 등록')}</RegisterText>
                </RegisterBox>
              </Section>
            </Card>
          </ModalContent>
        </ModalContainer>
      </Modal>

      {/* 등록/수정 서브 모달 */}
      <LogoSealEditModal
        open={!!editTarget}
        branchId={branchId}
        type={editTarget?.type}
        item={editTarget?.item}
        onClose={() => setEditTarget(null)}
      />

      {/* 삭제 확인 */}
      <CommonDeleteModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title={t('정말 삭제하시겠습니까?')}
        description={t('삭제한 항목은 복구할 수 없습니다.')}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default LogoSealModal;
